import { useMemo, useState } from 'react';

import { apiClient } from '../../core/libs/api';
import { HttpResource } from '../../core/libs/http-resource';
import { AppNotify } from '../../features/app-notify/app-notify';
import { getNameLocal } from '../../lib/local/get-title';
import { useLocal } from '../../lib/local/use-local';
import { getTripDetailsRequest } from '../available-routes/requests/get-trip-details.request';
import { Station, TripData } from './types';

type UsePaymentDataParams = {
  startStationId: string;
  endStationId: string;
};

export const useTripData = ({
  startStationId,
  endStationId,
}: UsePaymentDataParams) => {
  const [lang] = useLocal();
  const [tripData, setTripData] = useState<TripData>({
    trip: {
      trainType: { nameRu: '', id: -1 },
      startTime: new Date().toJSON(),
      finishTime: new Date().toJSON(),
    },
    route: { nameRu: '', id: 0, number: '' },
    routeStations: [],
    routeStationZones: [],
    tariffs: [],
  });

  const [tripRequest] = useState(
    () =>
      new HttpResource<string, []>({
        apiClient,
        httpRequest: getTripDetailsRequest,
        errorNotify: new AppNotify(),
        onFinish(result: any) {
          setTripData(result);
        },
      })
  );

  const startStation = useMemo(
    () =>
      tripData.routeStations.find(
        ({ station }: { station: Station }) =>
          String(station.id) === startStationId
      )?.station,
    [startStationId, tripData.routeStations]
  );

  const endStation = useMemo(
    () =>
      tripData.routeStations.find(
        ({ station }: { station: Station }) =>
          String(station.id) === endStationId
      )?.station,
    [endStationId, tripData.routeStations]
  );

  const routeZone = useMemo(
    () =>
      tripData.routeStationZones.find(
        ({ stationFromId, stationToId }) =>
          stationFromId === startStation?.id && stationToId === endStation?.id
      ),
    [endStation?.id, startStation?.id, tripData.routeStationZones]
  );

  const tariffs = useMemo(() => {
    const result: any = [];

    const foundTariffs = tripData.tariffs.filter(
      ({ trainType, zone }) =>
        trainType.id === tripData.trip.trainType.id &&
        zone.id === routeZone?.zoneId
    );

    foundTariffs.forEach(
      ({ id, price, tariffType, maxCountPerTicket, discountRules }) => {
        if (result.find((tariff: any) => tariffType.id === tariff.tariffTypeId))
          return;

        result.push({
          id,
          price,
          maxCountPerTicket,
          discountRules,
          tariffTypeId: tariffType.id,
          main: tariffType.main,
          name: getNameLocal(lang, tariffType),
        });
      }
    );

    return result;
  }, [lang, routeZone?.zoneId, tripData.tariffs, tripData.trip.trainType.id]);

  const trip = {
    startTime: tripData.trip.startTime,
    finishTime: tripData.trip.finishTime,
    trainType: { name: getNameLocal(lang, tripData.trip.trainType) },
    route: {
      id: tripData.route.id,
      name: getNameLocal(lang, tripData.route),
      nameRu: getNameLocal(lang, tripData.route),
      number: tripData.route.number,
    },
    wagons: tripData.trip.wagons,
  };

  return [
    {
      startStation,
      endStation,
      trip,
      tariffs,
      // routeZone,
    },
    tripRequest,
  ] as const;
};
